import React from "react";
import { motion } from "framer-motion";
import { FaGithub } from "react-icons/fa";

export default function Achievements() {
  const achievements = [
    {
      title: "Postathon (Indian Post) – 2nd Place",
      description:
        "Won 2nd place among 150+ teams by building an NLP-based complaint analysis system with automated intent detection, categorization, and priority tagging.",
      tech: ["React", "Node.js", "NLP", "PostgreSQL"],
      demo: "https://hackathon-postathon.vercel.app/",
      github: "https://github.com/vinodpachipala1/hackathon-Postathon",
    },
    {
      title: "AQI-Sense Pro – Top 15/200 Teams",
      description:
        "Ranked Top 15 among 200+ teams by developing an AI-powered AQI assistant delivering real-time air quality insights, smart alerts, and personalized health recommendations.",
      tech: ["React", "Node.js", "AI", "APIs"],
      demo: "https://aqi-sense-pro.vercel.app/",
      github: "https://github.com/vinodpachipala1/InnovateX-Hackathon",
    },
  ];

  return (
    <motion.section
      id="achievements"
      className="py-24 px-4 text-white relative"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      {/* Title */}
      <div className="text-center mb-16">
        <motion.h2
          className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent inline-block"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Hackathons & Achievements
        </motion.h2>
        <motion.div
          className="h-1 w-24 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mt-4 rounded-full"
          initial={{ width: 0 }}
          whileInView={{ width: 96 }}
          transition={{ duration: 0.8 }}
        />
      </div>

      {/* Cards – narrower container (max-w-4xl) */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          visible: { transition: { staggerChildren: 0.1 } },
        }}
      >
        {achievements.map((item, index) => (
          <motion.div
            key={index}
            className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            {/* Subtle gradient overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-600/10 to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-2xl pointer-events-none" />

            {/* Live Preview – improved scaling */}
            {item.demo && (
              <div className="relative w-full h-44 overflow-hidden bg-gray-900 rounded-t-2xl">
                <a
                  href={item.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 z-10"
                >
                  <iframe
                    src={item.demo}
                    className="pointer-events-none absolute top-0 left-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    title={item.title}
                  />
                </a>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-40" />
              </div>
            )}

            {/* Content – tighter padding */}
            <div className="p-5 relative z-10">
              <h3 className="text-lg md:text-xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent group-hover:from-purple-400 group-hover:to-pink-400 transition-all duration-300">
                {item.title}
              </h3>

              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                {item.description}
              </p>

              {/* Tech Stack – rounded-full badges */}
              <div className="flex flex-wrap gap-2 mb-5">
                {item.tech.map((tech, i) => (
                  <span
                    key={i}
                    className="text-xs px-2.5 py-1 bg-gray-800/80 text-gray-300 rounded-full border border-gray-700 group-hover:border-purple-500/30 transition-all duration-300"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* Links */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-700 group-hover:border-purple-500/30 transition-colors">
                {item.demo && (
                  <a
                    href={item.demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 text-sm font-medium inline-flex items-center gap-1 transition-all hover:gap-2 group/link"
                  >
                    Live Demo
                    <span className="inline-block transition-transform group-hover/link:translate-x-1">→</span>
                  </a>
                )}

                {item.github && (
                  <a
                    href={item.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-all hover:scale-110 hover:rotate-3"
                    aria-label="GitHub"
                  >
                    <FaGithub size={20} />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  );
}